import { Injectable } from '@angular/core';
import { ApplicationInsights, IDependencyTelemetry, IPageViewTelemetry, ITraceTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ActivatedRouteSnapshot, EventType, NavigationEnd, NavigationStart, ResolveEnd, ResolveStart } from '@angular/router';
import { environment } from '@environments/environment';
import { HttpErrorResponse, HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { MatDialogRef } from '@angular/material/dialog';

// ROUTER_EVENT_NAMES used to provide unminified names
// for logging purposes

const ROUTER_EVENT_NAMES: Record<number, string> = {
  [EventType.NavigationStart]: 'NavigationStart',
  [EventType.NavigationEnd]: 'NavigationEnd',
  [EventType.NavigationCancel]: 'NavigationCancel',
  [EventType.NavigationError]: 'NavigationError',
  [EventType.RoutesRecognized]: 'RoutesRecognized',
  [EventType.ResolveStart]: 'ResolveStart',
  [EventType.ResolveEnd]: 'ResolveEnd',
  [EventType.GuardsCheckStart]: 'GuardsCheckStart',
  [EventType.GuardsCheckEnd]: 'GuardsCheckEnd',
  [EventType.RouteConfigLoadStart]: 'RouteConfigLoadStart',
  [EventType.RouteConfigLoadEnd]: 'RouteConfigLoadEnd',
  [EventType.ChildActivationStart]: 'ChildActivationStart',
  [EventType.ChildActivationEnd]: 'ChildActivationEnd',
  [EventType.ActivationStart]: 'ActivationStart',
  [EventType.ActivationEnd]: 'ActivationEnd',
  [EventType.Scroll]: 'Scroll',
  [EventType.NavigationSkipped]: 'NavigationSkipped',
};

@Injectable()
export class ApplicationInsightsService {
  private appInsights = new ApplicationInsights({
    config: {
      disableTelemetry: environment.appInsightsDisableTelemetry,
      instrumentationKey: environment.appInsightsInstrumentationKey,
    }
  });

  constructor() {
    this.appInsights.loadAppInsights();
  }

  setAuthenticatedUserContext(userId: string, accountId: string) {
    this.appInsights.setAuthenticatedUserContext(userId, accountId);
  }

  clearAuthenticatedUserContext() {
    this.appInsights.clearAuthenticatedUserContext();
  }

  logHttpEvent(startTime: number, requestId: string, req: HttpRequest<unknown>, event: HttpEvent<unknown>) {
    if (!(event instanceof HttpRequest 
      || event instanceof HttpResponse 
      || event instanceof HttpErrorResponse))
      return;
    
    const urlObj = new URL(req.urlWithParams);

    const type: string = ROUTER_EVENT_NAMES[event.type];
    const id: string = requestId;
    const name: string = `${req.method} ${urlObj.pathname}`;
    const target: string = urlObj.origin;
    const data: string = req.urlWithParams;
    const duration: number = Date.now() - startTime;
    const success: boolean = !(event instanceof HttpErrorResponse);
    const responseCode: number = event instanceof HttpResponse
      ? event.status
      : 0;
    
    this.trackDependency(
      type,
      id,
      name,
      target,
      data,
      duration,
      success,      
      responseCode);
  }

  logNavigationEvent(event: any, snapshot: ActivatedRouteSnapshot) {
    if (!(event instanceof NavigationStart 
      || event instanceof NavigationEnd))
      return;

    var name: string = null;
    const eventType: string = ROUTER_EVENT_NAMES[event.type];

    if (snapshot != null) {
      const activatedComponent = this.getActivatedComponent(snapshot);

      if (activatedComponent?.componentName != null)
        name = activatedComponent.componentName;

      const routeTemplate = this.getRouteTemplate(snapshot);

      if (routeTemplate?.length > 0) {
        if (name?.length > 0)
          name += " " + routeTemplate;
        else
          name = routeTemplate;;
      }
    }
    
    if (event instanceof NavigationStart) {
      this.logPageView(
        name ?? event.url,
        event.url,
        { type: eventType, id: event.id });
    }
    else if (event instanceof NavigationEnd) {
      this.logPageView(
        name ?? event.urlAfterRedirects,
        event.urlAfterRedirects,
        { type: eventType, id: event.id });
    }
  }

  logOpenDialog(dialogRef: MatDialogRef<any, any>) {
    const name: string = 'Dialog ' + dialogRef.id;
    const url: URL = new URL(window.location.href)
    const uri: string = url.origin + url.pathname;

    this.logPageView(
      name,
      uri);
  }

  logPageView(name?: string, uri?: string, properties?: { [key: string]: any }) {
    properties = this.addHref(properties);

    const telemetry: IPageViewTelemetry = {
      name, 
      uri, 
      properties
    };

    this.appInsights.trackPageView(telemetry);
  }

  trackTraceInformation(message: string, properties?: { [key: string]: any }) {
    const severityLevel: SeverityLevel = SeverityLevel.Information;
    
    properties = this.addHref(properties);

    var telemetry: ITraceTelemetry = {
      message, 
      severityLevel,
      properties
    };

    this.appInsights.trackTrace(telemetry);
  }

  trackTraceError(message: string, properties?: { [key: string]: any }) {
    const severityLevel: SeverityLevel = SeverityLevel.Error;

    properties = this.addHref(properties);

    var telemetry: ITraceTelemetry = { 
      message, 
      severityLevel,
      properties
    };

    this.appInsights.trackTrace(telemetry);
  }

  trackDependency(type: string, id: string, name: string, target: string, data: string, duration: number, success: boolean, responseCode: number, properties?: { [key: string]: any }) {
    properties = this.addHref(properties);

    const telemetry: IDependencyTelemetry = {
      id: id,
      name: name,
      target: target,
      data: data,
      duration: duration,
      success: success,
      responseCode: responseCode,
      type: type,
      properties: properties
    };

    this.appInsights.trackDependencyData(telemetry);
  }
    
  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    var path: string = null;

    if (snapshot.routeConfig?.path?.length > 0) {
      path = snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      const routeTemplate = this.getRouteTemplate(snapshot.firstChild);

      if (routeTemplate?.length > 0) {
        if (path?.length > 0)
          path += "/" + routeTemplate;
        else
          path = routeTemplate;
      }
    }

    return path;
  }

  private addHref(properties?: { [key: string]: any }) {
    if (properties != null && properties["href"] != null)
      return;
    
    const href: string = window.location.href;

    if (properties == null)
      properties = { href };
    else
      properties = { ...properties, href };

    return properties;
  }
}
